import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRequestPasswordResetMutation } from '../../store/api/localApi';

type ResetError =
  | { kind: 'invite-not-accepted'; message: string }
  | { kind: 'generic'; message: string };

const parseError = (error: unknown): ResetError => {
  const fallback: ResetError = {
    kind: 'generic',
    message: 'Could not send reset code. Please try again.',
  };
  if (!error || typeof error !== 'object') return fallback;
  const e = error as { data?: any; message?: unknown };
  // Backend returns { error: 'INVITE_NOT_ACCEPTED', message: ... } with 409
  // when the user hasn't completed their invitation signup yet. We surface
  // that distinctly so we can offer a path forward instead of a dead-end.
  const data = e.data && typeof e.data === 'object' ? e.data : null;
  if (data?.error === 'INVITE_NOT_ACCEPTED') {
    return {
      kind: 'invite-not-accepted',
      message: typeof data.message === 'string' ? data.message : fallback.message,
    };
  }
  if (typeof e.data === 'string') return { kind: 'generic', message: e.data };
  if (data && typeof data.message === 'string') {
    return { kind: 'generic', message: data.message };
  }
  if (data && typeof data.error === 'string') {
    return { kind: 'generic', message: data.error };
  }
  if (typeof e.message === 'string') return { kind: 'generic', message: e.message };
  return fallback;
};

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<ResetError | null>(null);
  const [requestReset, { isLoading }] = useRequestPasswordResetMutation();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await requestReset({ email }).unwrap();
      // Carry the email forward so the next page doesn't have to re-ask.
      // Using a query string keeps it bookmark-friendly without leaking
      // anything sensitive (the code itself goes via email).
      navigate(`/reset-password?email=${encodeURIComponent(email)}`);
    } catch (err) {
      setError(parseError(err));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter the email on your account. We'll send you a 6-digit code to confirm it's you.
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error?.kind === 'invite-not-accepted' && (
            // Invitation not yet completed — we can't reset their password
            // because Cognito hasn't moved the user to a confirmed state. Point
            // them at the invite email rather than letting them keep retrying.
            <div
              data-testid="forgot-password-invite-pending"
              className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded space-y-2"
            >
              <p className="text-sm font-medium">Finish setting up your account</p>
              <p className="text-sm">{error.message}</p>
            </div>
          )}
          {error?.kind === 'generic' && (
            <div
              data-testid="forgot-password-error"
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded"
            >
              {error.message}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              data-testid="forgot-password-email"
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Enter your email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !email}
            data-testid="forgot-password-submit"
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Sending…' : 'Send reset code'}
          </button>

          <div className="text-center text-sm">
            <Link
              to="/login"
              data-testid="forgot-password-back"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
